import React, { useEffect, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAppDispatch } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { ParsedAddress } from 'types/baseTypes';
import { formatBillingDetails } from '../Utils';
import { showNotification } from 'utils/Notifier';

interface StripeCardComponentProps {
  onSuccess?: (paymentMethodId?: string) => void;
  onError?: () => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  shouldSubmit?: boolean;
  className?: string;
  billingAddress?: ParsedAddress;
  cardholderName?: string;
  cardholderEmail?: string;
}

const StripeCardComponent: React.FC<StripeCardComponentProps> = ({
  onSuccess,
  onError,
  onProcessingChange,
  shouldSubmit = false,
  className = '',
  billingAddress,
  cardholderName,
  cardholderEmail
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const [isProcessing, setIsProcessing] = useState(false);

  // Notify parent of processing state changes
  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  // Handle submission when shouldSubmit changes to true
  useEffect(() => {
    if (shouldSubmit && !isProcessing) {
      handleSubmit();
    }
  }, [shouldSubmit, isProcessing]);

  const handleSubmit = async (): Promise<void> => {
    if (!stripe || !elements) {
      const errorMessage = 'Payment system is still loading. Please wait a moment and try again.';
      showNotification(dispatch, NotifierTypes.WARNING, errorMessage, 3000);
      onError?.();
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      showNotification(dispatch, NotifierTypes.ERROR, 'Card element not found');
      onError?.();
      return;
    }

    setIsProcessing(true);

    try {
      const billingDetails = formatBillingDetails(billingAddress, cardholderName, cardholderEmail);

      // Create payment method with card element
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: billingDetails || undefined
      });

      if (error) {
        const errorMessage = error.message || 'Failed to save card. Please try again.';
        showNotification(dispatch, NotifierTypes.ERROR, errorMessage);

        // Scroll to card element for better UX
        const cardElementContainer = document.getElementById('customer-cof-card-element');
        if (cardElementContainer) {
          cardElementContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        onError?.();
        return;
      }

      if (paymentMethod) {
        onSuccess?.(paymentMethod.id);
        return;
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred. Please try again.';
      showNotification(dispatch, NotifierTypes.ERROR, errorMessage);
      onError?.();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div id="customer-cof-card-element" className="space-y-4">
      <div>
        <div id="card-element-label" className="mb-2 text-sbase font-semibold text-headingGray">
          Card information
        </div>
        <CardElement
          id="card-element"
          options={{
            style: {
              base: {
                fontSize: '15px',
                backgroundColor: 'transparent',
                color: '#333333',
                '::placeholder': {
                  color: '#828282'
                }
              }
            },
            disableLink: true,
            hidePostalCode: true
          }}
          className={`rounded-md border border-borderGray bg-white ${className}`}
        />
      </div>
    </div>
  );
};

StripeCardComponent.displayName = 'StripeCardComponent';

export default StripeCardComponent;
